import { useState } from "react";
import { Link, useLocation } from "react-router";
import { describeCode } from "../api/errors";
import { visibleBody } from "../api/redaction";
import type { MixOutputTrack, Playback, QueueItem, RepeatMode } from "../api/types";
import { useCommands, useGate, useMe, usePosition, useServices, useSync } from "../app/services";
import { usePlaybackActions } from "../app/usePlaybackActions";
import { canRemoveItem, voiceName } from "../lib/gates";
import { kindLabel, REPEAT_LABEL, performerNames } from "../lib/labels";
import { playerCondition } from "../lib/playerStatus";
import { isDedicatedMix, mixDescription, mixEnabled, mixLabel, mixPauseDescription, mixTransportButton } from "../lib/mix";
import { formatMs, parseTime, speakMs } from "../lib/time";
import { Highlight } from "../motion/Highlight";
import { isInFlight, type TrackedCommand } from "../state/commands";
import { Ribatan } from "./stage";
import { Icon } from "./Icon";
import { MixPreferences } from "./mixPreferences";
import { SaveButton } from "./cards";
import { Dialog, Field, Pill, Thumb } from "./ui";

/* ---------------------------------------------------------------- 再生位置 */

export function ProgressBar({ position, length, stale, label = "再生位置", approximate = false }: {
  position: number | null; length: number; stale: boolean; label?: string; approximate?: boolean;
}) {
  const pos = Math.max(0, Math.min(length, position ?? 0));
  const pct = length > 0 ? Math.min(100, (pos / length) * 100) : 0;
  return (
    <div className="progress">
      <div
        className={`progress__track${stale ? " progress__track--stale" : ""}`}
        role="progressbar"
        aria-label={stale ? `${label}（更新停止中のため止めています）` : label}
        aria-valuemin={0}
        aria-valuemax={Math.round(length / 1000)}
        aria-valuenow={Math.round(pos / 1000)}
        aria-valuetext={`${approximate ? "約 " : ""}${speakMs(pos)} ／ ${speakMs(length)}`}
      >
        <span className="progress__fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="progress__times" aria-hidden="true">
        <span>{approximate ? "約 " : ""}{formatMs(pos)}</span>
        <span>{formatMs(length)}</span>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- 固定プレイヤー（画面の下） */

export function MiniPlayer() {
  const sync = useSync();
  const position = usePosition();
  const location = useLocation();
  const gate = useGate("transport");
  const actions = usePlaybackActions();
  const commands = useCommands();
  if (location.pathname === "/now") return null;
  const p = sync.playback;
  const cond = playerCondition(sync);
  const dedicated = isDedicatedMix(p);
  const current = dedicated ? null : p?.current ?? null;
  const mixTracks = dedicated && p?.mix?.output?.status === "confirmed" ? p.mix.output.tracks : [];
  const stale = sync.conn !== "live";
  const length = current ? current.performance.segment.end_ms - current.performance.segment.start_ms : 0;
  const busy = commands.some((c) => (c.kind === "pause" || c.kind === "resume" || c.kind === "play") && isInFlight(c));
  const playing = dedicated ? p!.mix!.pause_reasons.length === 0 : p?.state === "playing";
  const transport = dedicated ? mixTransportButton(p!.mix!) : { kind: playing ? "pause" : "resume", label: playing ? "一時停止" : "再開" };
  const canToggle = dedicated || !!current && (p?.state === "playing" || p?.state === "paused");

  return (
    <section className={`mini-player mini-player--${cond.tone}`} aria-label="固定プレイヤー">
      {current && (
        <span className="mini-player__line" style={{ width: `${length ? ((position ?? 0) / length) * 100 : 0}%` }} aria-hidden="true" />
      )}
      <Link className="mini-player__main" to="/now">
        {current ? (
          <Thumb videoId={current.performance.segment.video_id} title={current.performance.work.title} size="s" />
        ) : mixTracks[0] ? (
          <Thumb videoId={mixTracks[0].source_video_id} title={mixTracks[0].title} size="s" />
        ) : (
          <span className="mini-player__icon">
            <Icon name={cond.icon} />
          </span>
        )}
        <span className="mini-player__text">
          {current ? (
            <>
              <span className="mini-player__title">{current.performance.work.title}</span>
              <span className="mini-player__sub">
                {performerNames(current.performance)}
                {p?.voice_channel_id && <> ・ {voiceName(p.voice_channel_name, p.voice_channel_id)}</>}
              </span>
            </>
          ) : (
            <>
              <span className="mini-player__title">{mixTracks.length ? mixTracks.map((track) => track.title).join(" / ") : cond.title}</span>
              <span className="mini-player__sub">{mixTracks.length ? "DJ Mix · いま聴くを開く" : "いま聴くを開く"}</span>
            </>
          )}
        </span>
        {(stale || (current && cond.kind !== "playing")) && (
          <span className="mini-player__state">
            <Pill tone={cond.tone === "danger" ? "danger" : cond.tone === "warn" ? "warn" : "info"} icon={cond.icon}>
              {cond.title}
            </Pill>
          </span>
        )}
        {current && playing && !stale && (
          <span className="mini-player__live" data-loop aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        )}
        <span className="visually-hidden">（いま聴くを開く）</span>
      </Link>
      {canToggle && (
        <button
          type="button"
          className="icon-btn icon-btn--solid mini-player__toggle"
          onClick={() => (transport.kind === "pause" ? actions.pause() : actions.resume())}
          disabled={!gate.ok || busy}
          aria-busy={busy || undefined}
          aria-label={transport.label}
          aria-describedby={!gate.ok ? "mini-player-gate" : undefined}
        >
          <Icon name={transport.kind === "pause" ? "pause" : "play"} />
        </button>
      )}
      {!gate.ok && canToggle && (
        <span id="mini-player-gate" className="visually-hidden">
          {gate.text}
        </span>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------- いま流れている曲 */

function MixTrackCard({ track, playback, main, stale, condition, trackCount }: {
  track: MixOutputTrack; playback: Playback; main: boolean; stale: boolean;
  condition: ReturnType<typeof playerCondition>; trackCount: number;
}) {
  const position = usePosition(track);
  const estimated = track.mapping_accuracy !== "exact";
  return (
    <section className="mix-output__track" aria-label={track.title}>
      <div className="now-card__top">
        <div className="now-card__art">
          <Thumb videoId={track.source_video_id} title={track.title} size="l" />
          {main && <p className="now-card__meta">
            <Pill
              tone={condition.tone === "ok" ? "calm" : condition.tone === "danger" ? "danger" : condition.tone === "warn" ? "warn" : "info"}
              icon={condition.icon}
            >{condition.title}</Pill>
            {trackCount > 1 && <Pill tone="info">{trackCount}曲をミックス中</Pill>}
          </p>}
        </div>
        <div className="now-card__head">
          <h2 className="now-card__title"><a href={track.source_url} target="_blank" rel="noreferrer">{track.title}</a></h2>
          {track.performers.length > 0 && <p className="now-card__performer">{track.performers.join(" / ")}</p>}
        </div>
      </div>
      <div className="now-card__body">
        <ProgressBar
          position={position}
          length={track.source_end_ms - track.source_start_ms}
          stale={stale}
          label={`${track.title}の再生位置${estimated ? "（推定）" : ""}`}
        />
        {estimated && <p className="mix-output__estimate">再生位置は目安です</p>}
        <div className="mix-output__actions">
          <SaveButton label={`「${track.title}」`} performanceId={track.performance_id}
            disabledReason={stale ? "最新の曲を確認してから保存できます。" : undefined}
            target={{ mix_target: {
              session_id: playback.session_id, generation: playback.generation, mix_id: playback.mix!.mix_id,
              plan_id: track.plan_id, occurrence_id: track.occurrence_id,
            } }} />
        </div>
      </div>
    </section>
  );
}

export function NowPlayingCard() {
  const sync = useSync();
  const position = usePosition();
  const p = sync.playback;
  const cond = playerCondition(sync);
  const current = isDedicatedMix(p) ? null : p?.current ?? null;
  const stale = sync.conn !== "live";

  if (!current && isDedicatedMix(p) && p?.mix?.output?.status === "confirmed" && p.mix.output.tracks.length) {
    const output = p.mix.output;
    const trackCount = output.tracks.length + output.additional_tracks;
    return (
      <article className="now-card mix-output" aria-label="DJ Mixで流れている曲">
        <div className="mix-output__tracks">
          {output.tracks.map((track, index) => <MixTrackCard
            key={JSON.stringify([p.session_id, p.generation, p.mix!.mix_id, track.plan_id, track.occurrence_id])}
            track={track} playback={p} main={index === 0} stale={stale} condition={cond} trackCount={trackCount} />)}
        </div>
        <div className="now-card__body mix-output__footer">
          {output.additional_tracks > 0 && <p className="mix-output__note">ほか {output.additional_tracks} 曲が重なっています。</p>}
          {(stale || p.mix.pause_reasons.length > 0) && <p className="mix-output__note">{stale ? "更新停止中 · 最後に確認できた位置です" : "一時停止中"}</p>}
          <p className="mix-output__elapsed">Mix再生時間 <span>{formatMs(p.mix.confirmed_played_ms)}</span></p>
        </div>
      </article>
    );
  }

  if (!current) {
    return (
      <div className={`now-card now-card--empty now-card--${cond.tone}`}>
        {cond.kind === "empty_queue" || cond.kind === "idle" ? (
          <>
            <Ribatan pose="wait" className="now-card__ribatan" sizes="140px" />
            <p className="now-card__say">
              <span className="visually-hidden">りばたん：</span>つぎは、なにを聴く？
            </p>
          </>
        ) : cond.kind === "bot_offline" ? (
          <Ribatan pose="lost" className="now-card__ribatan" sizes="140px" />
        ) : (
          <Icon name={cond.icon} className="now-card__empty-icon" />
        )}
        <p className="now-card__empty-title">{cond.title}</p>
        {cond.detail && <p className="now-card__empty-detail">{cond.detail}</p>}
        {p?.mix?.output && p.mix.output.status !== "confirmed" && <p className="now-card__empty-detail">再生中の曲を確認しています。</p>}
        {p?.mix && <p className="now-card__empty-detail">Mix再生時間 {formatMs(p.mix.confirmed_played_ms)}</p>}
        {cond.kind === "empty_queue" && (
          <Link className="btn btn-secondary btn-small" to="/">
            おすすめから曲を探す
          </Link>
        )}
      </div>
    );
  }

  const perf = current.performance;
  const length = perf.segment.end_ms - perf.segment.start_ms;
  const body = visibleBody(current.post);
  return (
    <article className="now-card" aria-labelledby="now-title">
      <div className="now-card__top">
        <div className="now-card__art">
          <Thumb videoId={perf.segment.video_id} title={perf.work.title} size="l" />
          <p className="now-card__meta">
            <Pill
              tone={cond.tone === "ok" ? "calm" : cond.tone === "danger" ? "danger" : cond.tone === "warn" ? "warn" : "info"}
              icon={cond.icon}
            >
              {cond.title}
            </Pill>
            <Pill tone="info">{kindLabel(perf.kind)}</Pill>
          </p>
        </div>
        <div className="now-card__head">
          <h2 id="now-title" className="now-card__title">
            <Link to={`/performances/${perf.id}`}>{perf.work.title}</Link>
          </h2>
          <p className="now-card__performer">{performerNames(perf)}</p>
        </div>
      </div>
      <div className="now-card__body">
        <ProgressBar position={position} length={length} stale={stale} />
        <p className="now-card__added">
          <Icon name="user" className="inline-icon" />
          {current.author_name} さんが追加
        </p>
        {p?.voice_channel_id && (
          <p className="now-card__vc">
            <Icon name="speaker" className="inline-icon" />
            {voiceName(p.voice_channel_name, p.voice_channel_id)}
          </p>
        )}
        {current.post &&
          (body !== null ? (
            <blockquote className="now-card__intro">
              <p>{body}</p>
              <footer>— {current.post.author_name} さんの紹介</footer>
            </blockquote>
          ) : (
            <p className="now-card__intro now-card__intro--gone">紹介の投稿は削除されたか、非表示になっています。</p>
          ))}
      </div>
    </article>
  );
}

/* ---------------------------------------------------------------- 見ている本人の状態 */

export function ViewerStatus() {
  const me = useMe();
  const sync = useSync();
  const gate = useGate("transport");
  const p = sync.playback;
  // Mix の操作制限は PlayerControls 内で一度だけ案内する。
  if (!me || isDedicatedMix(p)) return null;
  let text: string;
  if (gate.ok) {
    text = me.voice_channel_id
      ? `${voiceName(me.voice_channel_name, me.voice_channel_id)} に参加中。再生を操作できます。`
      : "再生を操作できます。";
    if (!p?.voice_channel_id && me.voice_channel_id)
      text = `${voiceName(me.voice_channel_name, me.voice_channel_id)} に参加中。再生すると、BotがこのVCに来ます。`;
  } else text = gate.text;
  const tone = gate.ok ? "calm" : gate.reason === "stale" || gate.reason === "loading" ? "info" : "warn";
  return (
    <p className={`viewer-status viewer-status--${tone}`}>
      <Icon name={gate.ok ? "check" : gate.reason === "stale" ? "wifiOff" : "speaker"} className="inline-icon" />
      <span>
        <span className="visually-hidden">あなたの状態：</span>
        {text}
        {gate.ok && me.can_dj && <span className="viewer-status__dj">DJ</span>}
      </span>
    </p>
  );
}

/* ---------------------------------------------------------------- 操作 */

function isBusy(cmds: TrackedCommand[], kinds: string[]) {
  return cmds.some((c) => kinds.includes(c.kind) && isInFlight(c));
}

export function PlayerControls() {
  const sync = useSync();
  const gate = useGate("transport");
  const dj = useGate("dj");
  const mix = useGate("mix");
  const actions = usePlaybackActions();
  const cmds = useCommands();
  const [seekOpen, setSeekOpen] = useState(false);
  const [confirm, setConfirm] = useState<null | "stop" | "clear">(null);
  const p = sync.playback;
  const dedicated = isDedicatedMix(p);
  const enabled = mixEnabled(p);
  const current = dedicated ? null : p?.current ?? null;
  const playing = p?.state === "playing";
  const paused = p?.state === "paused" || p?.state === "interrupted";
  const hasQueue = (sync.queue?.items.length ?? 0) > 0;
  const toggleBusy = isBusy(cmds, ["pause", "resume", "play"]);
  const describedBy = !gate.ok ? "controls-gate" : undefined;
  const sharedGate = dedicated && !gate.ok && !mix.ok && gate.reason === mix.reason;
  const gateText = dedicated && gate.reason === "playback_owner_required" && p?.playback_owner_name
    ? `一時停止やMixの変更は、オーナーの${p.playback_owner_name}さんが操作できます。`
    : gate.text;
  const mixGateId = sharedGate ? "controls-gate" : !mix.ok ? "mix-gate" : undefined;

  const primary = () => {
    if (dedicated) return mixTransportButton(p!.mix!).kind === "pause" ? actions.pause() : actions.resume();
    if (playing) return actions.pause();
    if (paused && current) return actions.resume();
    return actions.play();
  };
  const primaryLabel = dedicated ? mixTransportButton(p!.mix!).label : playing ? "一時停止" : paused && current ? "再開" : "再生";
  const primaryPause = dedicated ? mixTransportButton(p!.mix!).kind === "pause" : playing;
  const primaryDisabled = !gate.ok || toggleBusy || !dedicated && ((!current && !hasQueue) || p?.state === "loading");

  return (
    <div className={`controls${dedicated ? " controls--mix" : ""}`}>
      <div className="controls__row" role="group" aria-label="再生の操作">
        <button
          type="button"
          className="btn btn-primary controls__primary"
          onClick={primary}
          disabled={primaryDisabled}
          aria-busy={toggleBusy || undefined}
          aria-describedby={describedBy}
        >
          <Icon name={primaryPause ? "pause" : "play"} className="btn-icon" />
          {primaryLabel}
        </button>
        {!dedicated && <button
          type="button"
          className="btn btn-secondary"
          onClick={actions.skip}
          disabled={!gate.ok || dedicated || !current || isBusy(cmds, ["skip"])}
          aria-busy={isBusy(cmds, ["skip"]) || undefined}
          aria-describedby={describedBy}
        >
          <Icon name="skip" className="btn-icon" />
          スキップ
        </button>}
        {!dedicated && <CaptureButton />}
      </div>
      {!gate.ok && (
        <p id="controls-gate" className={`controls__gate controls__gate--${gate.reason}`}>
          {gateText}
        </p>
      )}

      <section className="mix-control" aria-labelledby="mix-title">
        <div className="mix-control__copy">
          <h2 id="mix-title">DJ Mix <span className="mix-control__state">{p?.mix ? mixLabel(p.mix) : enabled ? "オン" : "オフ"}</span></h2>
          {!dedicated && <p>VCにいるみんなの好みを混ぜて、次の曲を選びます。</p>}
          {p?.mix && <p className="mix-control__hint" role="status">{p.mix.phase === "active" ? "次の曲はおまかせ。解除すると、いまの曲を最後まで流して終了します。" : mixDescription(p.mix)} {mixPauseDescription(p.mix)}</p>}
          {p?.playback_owner_name && (!dedicated || gate.ok) && <p className="mix-control__hint">再生オーナー：{p.playback_owner_name}</p>}
          {p?.dj_mix_error && <p className="mix-control__error" role="status">選曲候補を取得できませんでした。時間を置いて自動で探し直します。</p>}
          {!mix.ok && !sharedGate && <p id="mix-gate" className="mix-control__hint">{mix.text}</p>}
        </div>
        <button
          type="button"
          className={`btn ${enabled && !dedicated ? "btn-primary" : "btn-secondary"}`}
          aria-pressed={enabled}
          aria-busy={isBusy(cmds, ["dj_mix"]) || undefined}
          aria-describedby={mixGateId}
          disabled={!mix.ok || isBusy(cmds, ["dj_mix"])}
          onClick={() => actions.djMix(!enabled)}
        >
          {enabled ? p?.mix?.phase === "active" ? "Mixを解除" : "DJ Mixをオフにする" : "DJ Mixをオンにする"}
        </button>
      </section>

      {p?.mix?.preferences && <MixPreferences key={`${p.mix.mix_id}:${p.mix.preferences.mix_id}`} mix={p.mix} view={p.mix.preferences} gateDescriptionId={mixGateId} />}

      {(!dedicated || dj.ok) && <details className="dj-tools">
        <summary>
          DJ・管理者の操作
          {!dj.ok && <span className="dj-tools__note">（{dj.reason === "dj_required" ? "DJのみ" : "いまは使えません"}）</span>}
        </summary>
        <div className="dj-tools__body">
          {!dj.ok && <p className="dj-tools__gate">{dj.text}</p>}
          <div className="controls__row">
            {!dedicated && <button
              type="button"
              className="btn btn-secondary btn-small"
              onClick={() => setSeekOpen(true)}
              disabled={!dj.ok || dedicated || !current || isBusy(cmds, ["seek"])}
            >
              <Icon name="seek" className="btn-icon" />
              位置を指定して移動
            </button>}
            {!dedicated && <RepeatControl disabled={!dj.ok || isBusy(cmds, ["repeat"])} value={p?.repeat ?? "off"} onChange={actions.repeat} />}
            <button
              type="button"
              className="btn btn-secondary btn-small"
              onClick={() => setConfirm("stop")}
              disabled={!dj.ok || !p?.voice_channel_id}
            >
              <Icon name="stop" className="btn-icon" />
              再生を終了
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-small"
              onClick={() => setConfirm("clear")}
              disabled={!dj.ok || !hasQueue}
            >
              <Icon name="trash" className="btn-icon" />
              未再生を全消去
            </button>
          </div>
        </div>
      </details>}

      {current && <SeekDialog open={seekOpen} onClose={() => setSeekOpen(false)} playback={p!} onSeek={actions.seek} />}
      <Dialog
        open={confirm !== null}
        title={confirm === "stop" ? "再生を終了しますか？" : "未再生のキューを全部消しますか？"}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setConfirm(null)}>
              やめる
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={!dj.ok || confirm === "stop" && !p?.voice_channel_id || confirm === "clear" && !hasQueue}
              onClick={() => {
                if (confirm === "stop") actions.stop();
                else actions.clear();
                setConfirm(null);
              }}
            >
              {confirm === "stop" ? "終了する" : "全消去する"}
            </button>
          </>
        }
      >
        <p>
          {confirm === "stop"
            ? dedicated ? "DJ Mixを含む再生を終了し、BotがVCから抜けます。未再生の手動キューは残ります。"
              : "BotがVCから抜け、いまの曲は終了の扱いになります。未再生のキューは残ります。"
            : "まだ流れていない曲をキューから外します。いまの曲と再生は止めません。"}
        </p>
      </Dialog>
    </div>
  );
}

function RepeatControl({ value, onChange, disabled }: { value: RepeatMode; onChange: (m: RepeatMode) => void; disabled: boolean }) {
  const modes: RepeatMode[] = ["off", "one", "queue"];
  return (
    <div className="segmented" role="group" aria-label={`リピート（いまは${REPEAT_LABEL[value]}）`}>
      {modes.map((m) => (
        <button key={m} type="button" aria-pressed={value === m} disabled={disabled || value === m} onClick={() => onChange(m)}>
          {m === "off" ? "なし" : m === "one" ? "1曲" : "キュー"}
        </button>
      ))}
    </div>
  );
}

/** ここ好き：押した瞬間の位置はサーバー（音声ワーカー）が決める。画面の推定位置は送らない */
export function CaptureButton() {
  const sync = useSync();
  const actions = usePlaybackActions();
  const cmds = useCommands();
  const { api } = useServices();
  const p = sync.playback;
  const connected = api.isConnected("moments.capture");
  const busy = isBusy(cmds, ["capture"]);
  const canCapture =
    connected &&
    sync.conn === "live" &&
    !isDedicatedMix(p) &&
    !!p?.current &&
    (p.state === "playing" || p.state === "paused") &&
    p.worker_available &&
    !p.syncing;
  let why = "";
  if (!connected) why = "ここ好きの記録は、まだAPIに接続されていません。";
  else if (sync.conn !== "live") why = "更新停止中は記録できません（どの瞬間か確かめられないため）。";
  else if (isDedicatedMix(p)) why = "DJ Mixの再生中は、ここ好きの記録は使えません。";
  else if (!p?.current) why = "いまは再生中の曲がありません。";
  else if (p.state === "loading") why = "読み込み中は記録できません。流れ始めてから押してください。";
  else if (!p.worker_available) why = "Botが停止しているため記録できません。";
  else if (p.syncing) why = "Botの同期中は記録できません。";
  else if (p.state !== "playing" && p.state !== "paused") why = "いまは記録できる状態ではありません。";
  const button = (
    <button
      type="button"
      className="btn btn-capture"
      onClick={actions.capture}
      disabled={!canCapture || busy}
      aria-busy={busy || undefined}
      aria-describedby="capture-note"
    >
      <Icon name="sparkle" className="btn-icon" />
      ここ好き
    </button>
  );
  return (
    <span className="capture">
      {/* 押せないときは輪を消す（CSS）。包みを付けはずしするとボタンが作り直され、フォーカスが外れるため、いつも包む */}
      <Highlight kind="ring">{button}</Highlight>
      <span id="capture-note" className="visually-hidden">
        {why || "押した瞬間の位置を、サーバーが自分だけの下書きとして記録します。"}
      </span>
    </span>
  );
}

/* ---------------------------------------------------------------- 位置を指定して移動（DJ） */

function SeekDialog({
  open,
  onClose,
  playback,
  onSeek,
}: {
  open: boolean;
  onClose: () => void;
  playback: Playback;
  onSeek: (ms: number) => void;
}) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const seg = playback.current!.performance.segment;
  const length = seg.end_ms - seg.start_ms;
  const submit = () => {
    const ms = parseTime(text);
    if (ms === null) return setError("「2:34」のように、分:秒で入力してください。");
    if (ms >= length) return setError(`区間の長さ（${formatMs(length)}）より前を指定してください。`);
    onSeek(ms);
    setText("");
    setError(null);
    onClose();
  };
  return (
    <Dialog
      open={open}
      title="位置を指定して移動"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            やめる
          </button>
          <button type="submit" form="seek-form" className="btn btn-primary">
            移動する
          </button>
        </>
      }
    >
      <form
        id="seek-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <p className="dialog__lead">VCで流れている曲の位置を移します。参加している全員の再生が動きます。</p>
        <Field label="区間の開始からの位置" hint={`0:00〜${formatMs(length)} の間で。例：2:34`} error={error} required>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              className="input input--time"
              inputMode="numeric"
              autoComplete="off"
              value={text}
              onChange={(e) => setText(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              placeholder="2:34"
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- キュー */

export function QueueList() {
  const sync = useSync();
  const me = useMe();
  const gate = useGate("control");
  const dj = useGate("dj");
  const actions = usePlaybackActions();
  const cmds = useCommands();
  const items = sync.queue?.items ?? [];
  const stale = sync.conn !== "live";
  const reorderBusy = isBusy(cmds, ["reorder"]);
  if (sync.playback?.mix?.phase === "active" && items.length === 0) return null;

  const move = (item: QueueItem, dir: -1 | 1) => {
    const ids = items.map((i) => i.id);
    const i = ids.indexOf(item.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    actions.reorder(ids, `「${item.performance.work.title}」を${dir < 0 ? "上" : "下"}へ`);
  };

  return (
    <section className="queue" aria-labelledby="queue-title">
      <h2 id="queue-title" className="section-title">
        {sync.playback?.mix ? "手動キュー" : "次に流れる曲"} <span className="count">{items.length}</span>
        {stale && (
          <Pill tone="warn" icon="wifiOff">
            更新停止中
          </Pill>
        )}
      </h2>
      {items.length === 0 ? (
        <p className="queue__empty">キューは空です。曲や投稿の「キューに追加」から入れられます。</p>
      ) : (
        <ol className="queue__list">
          {items.map((item, n) => {
            const perf = item.performance;
            const removable = gate.ok && canRemoveItem(me, item.owner_id);
            const body = visibleBody(item.post);
            return (
              <li key={item.id} className={`queue-item${perf.available ? "" : " queue-item--unavailable"}`}>
                <span className="queue-item__n" aria-hidden="true">
                  {n + 1}
                </span>
                <Thumb videoId={perf.segment.video_id} title={perf.work.title} size="s" />
                <div className="queue-item__body">
                  <p className="queue-item__title">
                    <span className="visually-hidden">{n + 1}曲目：</span>
                    <Link to={`/performances/${perf.id}`}>{perf.work.title}</Link>
                  </p>
                  <p className="queue-item__sub">
                    {performerNames(perf)} ・ {formatMs(perf.segment.end_ms - perf.segment.start_ms)} ・ {item.author_name} さん
                  </p>
                  {!perf.available && (
                    <p className="queue-item__warn">
                      <Icon name="warn" className="inline-icon" />
                      元動画を利用できないため、順番が来たら飛ばされます
                    </p>
                  )}
                  {item.post && body !== null && <p className="queue-item__intro">「{body}」</p>}
                </div>
                <div className="queue-item__actions">
                  {dj.ok && items.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="icon-btn icon-btn--small"
                        onClick={() => move(item, -1)}
                        disabled={n === 0 || reorderBusy}
                        aria-label={`「${perf.work.title}」を1つ上へ`}
                      >
                        <Icon name="up" />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn--small"
                        onClick={() => move(item, 1)}
                        disabled={n === items.length - 1 || reorderBusy}
                        aria-label={`「${perf.work.title}」を1つ下へ`}
                      >
                        <Icon name="down" />
                      </button>
                    </>
                  )}
                  {removable && (
                    <button
                      type="button"
                      className="icon-btn icon-btn--small"
                      onClick={() => actions.remove(item)}
                      disabled={cmds.some((c) => c.kind === "remove" && isInFlight(c) && c.label.includes(perf.work.title))}
                      aria-label={`「${perf.work.title}」をキューから外す`}
                    >
                      <Icon name="trash" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {!dj.ok && items.length > 1 && <p className="queue__note">並べ替え・他の人の曲の削除は、DJ・管理者だけができます。</p>}
    </section>
  );
}

/** 失敗のコードの説明（履歴などで使う） */
export function reasonText(code: string | null | undefined) {
  return describeCode(code);
}
